import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const schema = yup.object().shape({
  searchText: yup.string().max(200, "Máximo 200 caracteres"),
  author: yup.string().max(150, "Máximo 150 caracteres"),
  sourceCredit: yup.string().max(150, "Máximo 150 caracteres"),
  id: yup
    .string()
    .matches(/^$|^\d+$/, "El ID debe ser numérico"),
  keywords: yup.string().max(200, "Máximo 200 caracteres"),
  uploadDateStart: yup.date().nullable().transform((curr, orig) => (orig === "" ? null : curr)),
  uploadDateEnd: yup
    .date()
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .test("is-after-upload-start", "La fecha final de subida debe ser posterior a la inicial", function (value) {
      const { uploadDateStart } = this.parent;
      return !uploadDateStart || !value || value >= uploadDateStart;
    }),
  captureDateStart: yup.date().nullable().transform((curr, orig) => (orig === "" ? null : curr)),
  captureDateEnd: yup
    .date()
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .test("is-after-capture-start", "La fecha final de captura debe ser posterior a la inicial", function (value) {
      const { captureDateStart } = this.parent;
      return !captureDateStart || !value || value >= captureDateStart;
    }),
  itemType: yup.string().oneOf(["all", "image", "story"], "Tipo no válido"),
});

export default function SearchForm({ onSearch, isLoading }) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      searchText: "",
      author: "",
      sourceCredit: "",
      id: "",
      keywords: "",
      uploadDateStart: "",
      uploadDateEnd: "",
      captureDateStart: "",
      captureDateEnd: "",
      itemType: "all",
    },
  });

  const onSubmit = (data) => {
    // Convertir fechas vacías a null
    const searchParams = {
      ...data,
      author: data.author.trim(),
      sourceCredit: data.sourceCredit.trim(),
      id: data.id.trim() || null,
      keywords: data.keywords.trim(),
      uploadDateStart: data.uploadDateStart || null,
      uploadDateEnd: data.uploadDateEnd || null,
      captureDateStart: data.captureDateStart || null,
      captureDateEnd: data.captureDateEnd || null,
    };
    onSearch(searchParams);
  };

  const handleReset = () => {
    reset();
    onSearch({
      searchText: "",
      author: "",
      sourceCredit: "",
      id: null,
      keywords: "",
      uploadDateStart: null,
      uploadDateEnd: null,
      captureDateStart: null,
      captureDateEnd: null,
      itemType: "all",
    });
  };

  return (
    <div className="search-form-container">
      <form className="Form Form_search-archive" onSubmit={handleSubmit(onSubmit)}>
        <div className="Form-Container">
          <fieldset>
            <legend>Buscar en Archivo GN4</legend>

              <div className="Form-Row">
                <div className="Form-Field Form-Field--full">
                <label htmlFor="searchText" className="Form-Label">
                  Buscar por título o resumen
                </label>
                <input
                  id="searchText"
                  type="text"
                  className="Form-Input"
                  placeholder="Ingrese texto a buscar..."
                  {...register("searchText")}
                />
                {errors.searchText && (
                  <span className="Form-Error">{errors.searchText.message}</span>
                )}
                </div>
              </div>

              <div className="Form-Row">
                <div className="Form-Field">
                  <label htmlFor="author" className="Form-Label">Autor o creador</label>
                  <input id="author" type="text" className="Form-Input" placeholder="Nombre del autor..." {...register("author")} />
                  {errors.author && <span className="Form-Error">{errors.author.message}</span>}
                </div>
                <div className="Form-Field">
                  <label htmlFor="sourceCredit" className="Form-Label">Fuente, crédito o agencia</label>
                  <input id="sourceCredit" type="text" className="Form-Input" placeholder="AFP, Reuters, El Espectador..." {...register("sourceCredit")} />
                  {errors.sourceCredit && <span className="Form-Error">{errors.sourceCredit.message}</span>}
                </div>
                <div className="Form-Field">
                  <label htmlFor="id" className="Form-Label">ID del registro</label>
                  <input id="id" type="text" inputMode="numeric" className="Form-Input" placeholder="Ej. 1981699" {...register("id")} />
                  {errors.id && <span className="Form-Error">{errors.id.message}</span>}
                </div>
                <div className="Form-Field">
                  <label htmlFor="keywords" className="Form-Label">Keywords</label>
                  <input id="keywords" type="text" className="Form-Input" placeholder="Palabras clave..." {...register("keywords")} />
                  {errors.keywords && <span className="Form-Error">{errors.keywords.message}</span>}
                </div>
              </div>

              <div className="Form-Row">
                <div className="Form-Field">
                  <label htmlFor="uploadDateStart" className="Form-Label">Subida desde</label>
                  <input id="uploadDateStart" type="date" className="Form-Input" {...register("uploadDateStart")} />
                </div>
                <div className="Form-Field">
                  <label htmlFor="uploadDateEnd" className="Form-Label">Subida hasta</label>
                  <input id="uploadDateEnd" type="date" className="Form-Input" {...register("uploadDateEnd")} />
                </div>
              </div>

              <div className="Form-Row">
                <div className="Form-Field">
                  <label htmlFor="captureDateStart" className="Form-Label">Captura desde</label>
                  <input id="captureDateStart" type="date" className="Form-Input" {...register("captureDateStart")} />
                </div>
                <div className="Form-Field">
                  <label htmlFor="captureDateEnd" className="Form-Label">Captura hasta</label>
                  <input id="captureDateEnd" type="date" className="Form-Input" {...register("captureDateEnd")} />
                </div>
              </div>

              <div className="Form-Row">
              <div className="Form-Field">
                <label htmlFor="itemType" className="Form-Label">
                  Tipo de elemento
                </label>
                <select
                  id="itemType"
                  className="Form-Input"
                  {...register("itemType")}
                >
                  <option value="all">Todos</option>
                  <option value="image">Solo imágenes</option>
                  <option value="story">Solo historias</option>
                </select>
                {errors.itemType && (
                  <span className="Form-Error">{errors.itemType.message}</span>
                )}
              </div>
            </div>

            <div className="Form-Actions">
              <button
                type="submit"
                className="Button Button--primary"
                disabled={isLoading}
              >
                {isLoading ? "Buscando..." : "Buscar"}
              </button>
              <button
                type="button"
                className="Button Button--secondary"
                onClick={handleReset}
                disabled={isLoading}
              >
                Limpiar
              </button>
            </div>
          </fieldset>
        </div>
      </form>
    </div>
  );
}
